/**
 * Whether a colour chosen in the CMS can be drawn under the card's fixed ink.
 *
 * The assessment card pins its text to the light scheme's ink (`#20282E`) because its background
 * does not flip with the theme. That works for the five tints the design ships, which measure
 * between 7.42:1 and 9.72:1, but a colour typed into Directus answers to nobody: `#333333` gives
 * 1.18:1 and would ship as a card whose title cannot be read. The linter does not catch it either —
 * `check-a11y.ts` reports `color-contrast` as a warning.
 *
 * So the colour is measured before it is used, by the WCAG definition, and a failing one is simply
 * not applied: the card keeps the tint its id hashes to, which is a working card with the wrong
 * colour rather than a broken one.
 */

/** The ink every card is drawn with, from `--today-assessment-ink`. */
const INK = {red: 0x20, green: 0x28, blue: 0x2e};

/**
 * WCAG AA for normal text. The card's eyebrow and countdown are small, so this is the bar that
 * applies to them rather than the 3:1 of large text.
 */
const MINIMUM_RATIO = 4.5;

const SHORT_HEX = 4;
const FULL_HEX = 7;

function parseHex(colour: string): {red: number; green: number; blue: number} | null {
  const value = colour.trim();
  if (!/^#(?:[\da-f]{3}|[\da-f]{6})$/iu.test(value)) {
    return null;
  }
  const digits = value.length === SHORT_HEX
    ? [1, 2, 3].map((at) => value.slice(at, at + 1).repeat(2))
    : [value.slice(1, 3), value.slice(3, 5), value.slice(5, FULL_HEX)];
  const [red, green, blue] = digits.map((pair) => Number.parseInt(pair, 16));
  return {red: red ?? 0, green: green ?? 0, blue: blue ?? 0};
}

/** The WCAG relative luminance of a channel triplet. */
function luminance({red, green, blue}: {red: number; green: number; blue: number}): number {
  const channel = (value: number): number => {
    const ratio = value / 255;
    return ratio <= 0.039_28 ? ratio / 12.92 : ((ratio + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(red) + 0.7152 * channel(green) + 0.0722 * channel(blue);
}

/**
 * The colour if the ink stays legible on it, otherwise nothing.
 *
 * A colour it cannot read — a name, a gradient, an `rgb()` — is refused for the same reason a dark
 * one is: what cannot be measured cannot be shown to be readable. Only the hex notation Directus's
 * own colour interface produces is accepted.
 */
export function readableTint(colour: string | null | undefined): string | null {
  const parsed = parseHex(colour ?? '');
  if (parsed === null) {
    return null;
  }
  const [lighter, darker] = [luminance(parsed), luminance(INK)].sort((first, second) => second - first);
  const ratio = ((lighter ?? 0) + 0.05) / ((darker ?? 0) + 0.05);
  return ratio >= MINIMUM_RATIO ? colour ?? null : null;
}
